import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import ApprovalBox from './ApprovalBox'
import StageBar from './StageBar'
import TaskById from './TaskById'
import { formatDate, formatTime, plural } from '../lib/format'
import { useEscClose } from '../lib/useEscClose'
import { useAuth } from '../auth'
import {
  TICKET_STATUS_LABEL,
  WORK_STATUS_LABEL,
  type TicketDetail,
  type TicketStatus,
} from '../types'

export const STATUS_CLASS: Record<TicketStatus, string> = {
  new: 'tk-new',
  accepted: 'tk-accepted',
  in_progress: 'tk-progress',
  waiting_client: 'tk-waiting',
  resolved: 'tk-resolved',
  closed: 'tk-closed',
}

/**
 * Карточка обращения или заявки.
 *
 * Разложена на две колонки намеренно. Раньше всё шло одной лентой сверху вниз:
 * сведения, этапы, файлы, поля, согласование, задачи — и только в самом низу
 * переписка. Чтобы ответить клиенту, приходилось прокручивать мимо всего
 * остального, а чтобы свериться с телефоном — прокручивать обратно.
 *
 * Слева то, что делают: разговор с клиентом и ход согласования. Справа то, на
 * что смотрят: кто обратился, сроки, файлы, кто работает. Разговор прокручивается
 * сам в себе, поэтому поле ответа всегда на виду.
 */
export default function TicketDialog({
  id,
  mayReply,
  onClose,
}: {
  id: number
  mayReply: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [tab, setTab] = useState<'chat' | 'notes'>('chat')
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  // Задача, открытая поверх карточки. Не уводим со страницы: разговор с
  // клиентом остаётся под рукой, а закрыв задачу, человек возвращается ровно
  // туда, откуда пришёл.
  const [openTask, setOpenTask] = useState<number | null>(null)
  const bottom = useRef<HTMLDivElement>(null)

  useEscClose(() => {
    // Esc закрывает верхнее окно, а не оба сразу
    if (openTask === null) onClose()
  })

  const ticket = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.ticket(id),
    refetchInterval: 20_000,
  })
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['ticket', id] })
    qc.invalidateQueries({ queryKey: ['tickets'] })
  }

  const send = useMutation({
    mutationFn: () =>
      tab === 'chat' ? api.replyTicket(id, text.trim()) : api.noteTicket(id, text.trim()),
    onSuccess: () => {
      setText('')
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось отправить'),
  })

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.updateTicket(id, body),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const data = ticket.data
  const items = tab === 'chat' ? (data?.messages ?? []) : (data?.notes ?? [])

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [items.length, tab])

  return (
    <>
      <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal huge tkw">
          <div className="tkw-head">
            <div className="tkw-titles">
              <div className="tkw-num">
                {data ? `${data.kind_title} № ${id}` : `№ ${id}`}
                {data && (
                  <>
                    <span className="tk-cat-chip">{data.category_title}</span>
                    <span className={`tk-chip ${STATUS_CLASS[data.status]}`}>
                      {TICKET_STATUS_LABEL[data.status]}
                    </span>
                    {data.waiting_for_us && <span className="tkw-alarm">ждёт нашего ответа</span>}
                    {data.overdue && <span className="tkw-alarm">срок прошёл</span>}
                    {data.paid && <span className="tkw-ok">оплачено</span>}
                  </>
                )}
              </div>
              {data && <div className="tkw-subject">{data.subject}</div>}
            </div>
            <button className="tkw-close" title="Закрыть окно" onClick={onClose}>
              ✕
            </button>
          </div>

          {error && (
            <div className="error" style={{ margin: '10px 20px 0' }}>
              {error}
            </div>
          )}
          {ticket.isLoading && <p className="empty">Загрузка…</p>}

          {data && (
            <div className="tkw-body">
              {/* ---------------- слева: разговор ---------------- */}
              <div className="tkw-main">
                {data.steps.length > 0 && (
                  <StageBar steps={data.steps} dueOn={data.due_on} overdue={data.overdue} />
                )}

                <ApprovalBox
                  ticket={data}
                  mayReply={mayReply}
                  onChanged={refresh}
                  onError={setError}
                />

                <div className="tabs tkw-tabs">
                  <button className={tab === 'chat' ? 'active' : ''} onClick={() => setTab('chat')}>
                    Переписка с клиентом
                    {data.messages.length > 0 && (
                      <span className="tkw-count">{data.messages.length}</span>
                    )}
                  </button>
                  <button
                    className={tab === 'notes' ? 'active' : ''}
                    onClick={() => setTab('notes')}
                  >
                    Внутренние заметки
                    {data.notes.length > 0 && <span className="tkw-count">{data.notes.length}</span>}
                  </button>
                </div>

                <div className={`tkw-thread${tab === 'notes' ? ' tk-thread-notes' : ''}`}>
                  {tab === 'chat' && data.description && (
                    <div className="tkw-first">
                      <div className="tkw-first-label">Что просили сделать</div>
                      {data.description}
                    </div>
                  )}
                  {tab === 'notes' && (
                    <p className="hint" style={{ marginTop: 0 }}>
                      Клиент этого не видит. Заметки лежат отдельно от переписки — именно
                      чтобы не перепутать.
                    </p>
                  )}
                  {items.length === 0 && (
                    <p className="empty" style={{ padding: '18px 0' }}>
                      {tab === 'chat' ? 'Переписки пока нет' : 'Заметок пока нет'}
                    </p>
                  )}
                  {items.map((m: any) => (
                    <div
                      key={m.id}
                      className={
                        tab === 'notes'
                          ? 'tk-msg tk-msg-note'
                          : `tk-msg ${m.from_client ? 'tk-msg-theirs' : 'tk-msg-mine'}`
                      }
                    >
                      <div className="tk-msg-head">
                        {m.author_name ?? 'Неизвестно'}
                        <span className="tk-msg-time">
                          {formatDate(m.created_at)} {formatTime(m.created_at)}
                        </span>
                      </div>
                      <div className="tk-msg-body">{m.body}</div>
                    </div>
                  ))}
                  <div ref={bottom} />
                </div>

                {mayReply && (
                  <div className="tkw-reply">
                    <textarea
                      rows={3}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={
                        tab === 'chat'
                          ? 'Ответ клиенту — он увидит его в кабинете и получит письмом'
                          : 'Заметка для своих — клиент её не увидит'
                      }
                    />
                    <button
                      className="btn primary"
                      disabled={send.isPending || !text.trim()}
                      onClick={() => {
                        setError('')
                        send.mutate()
                      }}
                    >
                      {send.isPending
                        ? 'Отправляем…'
                        : tab === 'chat'
                          ? 'Ответить клиенту'
                          : 'Записать заметку'}
                    </button>
                  </div>
                )}
              </div>

              {/* ---------------- справа: сведения ---------------- */}
              <div className="tkw-aside">
                <Block title="Кто обратился">
                  <div className="tkw-contact">
                    <b>{data.contact?.company ?? data.client_name}</b>
                    <span>{data.contact?.full_name ?? 'завели сами'}</span>
                    {data.contact?.position && <span>{data.contact.position}</span>}
                    {data.contact?.phone && (
                      <a href={`tel:${data.contact.phone}`}>{data.contact.phone}</a>
                    )}
                    {data.contact?.email && (
                      <a href={`mailto:${data.contact.email}`}>{data.contact.email}</a>
                    )}
                  </div>
                  <div className="tkw-facts">
                    {data.program && <span>{data.program}</span>}
                    {/* У обуви и духов товарная группа и вид совпадают —
                        показывать одно и то же дважды незачем */}
                    {data.direction && data.direction !== data.program && (
                      <span>{data.direction}</span>
                    )}
                    {data.codes_count !== null && (
                      <span className="tk-count-inline">
                        заказать {data.codes_count}{' '}
                        {plural(data.codes_count, 'код', 'кода', 'кодов')}
                      </span>
                    )}
                    <span>создано {formatDate(data.created_at)}</span>
                  </div>
                </Block>

                {mayReply && (
                  <Block title="Ход дела">
                    <div className="grid-2">
                      {data.steps.length > 0 && (
                        <div className="field">
                          <label>Этап</label>
                          <select
                            value={data.stage ?? ''}
                            onChange={(e) => update.mutate({ stage: e.target.value })}
                          >
                            {data.steps.map((s) => (
                              <option key={s.stage} value={s.stage}>
                                {s.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="field">
                        <label>Ожидаемый срок</label>
                        <input
                          type="date"
                          value={data.due_on ?? ''}
                          onChange={(e) => update.mutate({ due_on: e.target.value || null })}
                        />
                      </div>
                      <div className="field">
                        <label>Состояние</label>
                        <select
                          value={data.status}
                          onChange={(e) => update.mutate({ status: e.target.value })}
                        >
                          {Object.entries(TICKET_STATUS_LABEL).map(([k, label]) => (
                            <option key={k} value={k}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field">
                        <label>Ведёт</label>
                        <select
                          value={data.assignee_id ?? ''}
                          onChange={(e) =>
                            update.mutate({ assignee_id: Number(e.target.value) || null })
                          }
                        >
                          <option value="">— не назначено —</option>
                          {users.data?.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.full_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Только у заявок. Обращение покрыто ежемесячной оплатой:
                          отмечать его оплаченным нечего и незачем. */}
                      {data.kind === 'order' && (
                      <div className="field full">
                        {/* Оплата отмечается руками: сверка с банком вне программы,
                            и угадывать за бухгалтерию — худшее, что можно сделать
                            с деньгами. */}
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={data.paid}
                            onChange={(e) => update.mutate({ paid: e.target.checked })}
                          />
                          <span>
                            <b>Оплачено</b>
                          </span>
                        </label>
                        <span className="hint-inline">
                          {data.paid && data.paid_at
                            ? `${formatDate(data.paid_at)}${data.paid_by ? ` · отметил ${data.paid_by}` : ''}. Снимете — вернётся в работу.`
                            : 'Отметка закрывает заявку'}
                        </span>
                      </div>
                      )}
                    </div>
                    {data.steps.length > 0 && (
                      <span className="hint-inline">
                        Смену этапа клиент увидит в кабинете и получит письмо
                      </span>
                    )}
                  </Block>
                )}

                <Block title="Файлы">
                  <StaffAttachments
                    ticket={data}
                    mayReply={mayReply}
                    onChanged={refresh}
                    onError={setError}
                  />
                </Block>

                <Block title="Работа по этой записи">
                  <Handover
                    ticket={data}
                    mayReply={mayReply}
                    onChanged={refresh}
                    onError={setError}
                    onOpenTask={setOpenTask}
                  />
                </Block>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Задача поверх карточки: соседний узел, чтобы клик по ней не долетал
          до подложки обращения и не закрывал его. */}
      {openTask !== null && <TaskById id={openTask} onClose={() => setOpenTask(null)} />}
    </>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="tkw-block">
      <div className="tkw-block-title">{title}</div>
      {children}
    </div>
  )
}

/**
 * Вложения к обращению. Заполненный шаблон помечен отдельно: именно по нему
 * считали количество кодов, и заказ уходит по этому файлу.
 */
function StaffAttachments({
  ticket,
  mayReply,
  onChanged,
  onError,
}: {
  ticket: TicketDetail
  mayReply: boolean
  onChanged: () => void
  onError: (e: string) => void
}) {
  const [busy, setBusy] = useState(false)

  async function upload(file: File | undefined, isResult = false) {
    if (!file) return
    setBusy(true)
    onError('')
    try {
      await api.attachToTicket(ticket.id, file, isResult)
      onChanged()
    } catch (e: any) {
      onError(e?.message ?? 'Не удалось приложить файл')
    } finally {
      setBusy(false)
    }
  }

  async function toggleResult(fileId: number, isResult: boolean) {
    setBusy(true)
    onError('')
    try {
      await api.markTicketFileResult(ticket.id, fileId, isResult)
      onChanged()
    } catch (e: any) {
      onError(e?.message ?? 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const files = ticket.files ?? []
  if (files.length === 0 && !mayReply) return <p className="hint">Файлов нет</p>

  return (
    <div className="tk-files">
      {files.length === 0 && <p className="hint">Файлов пока нет</p>}
      {files.map((f) => (
        <span key={f.id} className="tk-file-row">
          <button
            type="button"
            className={`tk-file${f.is_result ? ' result' : ''}`}
            onClick={() =>
              void api
                .downloadTicketFile(ticket.id, f.id, f.original_name)
                .catch((e: any) => onError(e?.message ?? 'Не удалось скачать файл'))
            }
            title={`${Math.max(1, Math.round(f.size / 1024))} КБ${
              f.uploaded_by ? ` · приложил ${f.uploaded_by}` : ''
            }`}
          >
            {f.is_result ? '✅' : f.is_template ? '📊' : '📎'} {f.original_name}
          </button>
          {/* Отметку ставят и задним числом: файл нередко кладут раньше, чем
              понимают, что он и есть итог работы. */}
          {mayReply && !f.is_template && (
            <button
              type="button"
              className="link-btn"
              disabled={busy}
              onClick={() => void toggleResult(f.id, !f.is_result)}
            >
              {f.is_result ? 'не результат' : 'это результат'}
            </button>
          )}
        </span>
      ))}
      {mayReply && (
        <div className="tkw-file-actions">
          <label className="btn small" style={{ cursor: 'pointer' }}>
            {busy ? 'Отправляем…' : '📎 Приложить файл'}
            <input
              type="file"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => void upload(e.target.files?.[0])}
            />
          </label>
          <label className="btn small primary" style={{ cursor: 'pointer' }}>
            ✅ Отправить результат
            <input
              type="file"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => void upload(e.target.files?.[0], true)}
            />
          </label>
        </div>
      )}
    </div>
  )
}

/**
 * Работа по заявке: кому передана и что сделано.
 *
 * Так замыкается круг. Клиент оставил заявку, специалист работает по ней в
 * привычном журнале — оценивает, считает время, — а здесь видно, что по ней
 * делается, и задачу можно открыть прямо отсюда.
 *
 * Открываем не у всех: чужую задачу видит только тот, кому позволено видеть
 * все. Рисовать кнопку, которая ответит «это не ваша задача», хуже, чем не
 * рисовать её вовсе.
 */
function Handover({
  ticket,
  mayReply,
  onChanged,
  onError,
  onOpenTask,
}: {
  ticket: TicketDetail
  mayReply: boolean
  onChanged: () => void
  onError: (e: string) => void
  onOpenTask: (taskId: number) => void
}) {
  const { user, canSeeAll } = useAuth()
  const [adding, setAdding] = useState(false)
  const [assignee, setAssignee] = useState('')
  const [estimate, setEstimate] = useState('')
  const [busy, setBusy] = useState(false)

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() })
  const staff = (users.data ?? []).filter((u) => u.is_active)
  const tasks = ticket.tasks ?? []

  async function hand() {
    if (!assignee) return
    setBusy(true)
    onError('')
    try {
      await api.handToSpecialist(ticket.id, {
        assignee_id: Number(assignee),
        estimate: estimate ? Number(estimate) : null,
      })
      setAdding(false)
      setAssignee('')
      setEstimate('')
      onChanged()
    } catch (e: any) {
      onError(e?.message ?? 'Не удалось передать')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {tasks.length === 0 && !adding && (
        <p className="hint" style={{ margin: '4px 0' }}>
          Пока никому не передано. Специалист работает в журнале — оттуда
          возьмутся часы и оценка.
        </p>
      )}

      {tasks.map((t) => {
        const можно = canSeeAll || t.assignee_id === user?.id
        return (
          <div
            key={t.id}
            className={`tkw-task${можно ? ' clickable' : ''}`}
            onClick={можно ? () => onOpenTask(t.id) : undefined}
            role={можно ? 'button' : undefined}
            tabIndex={можно ? 0 : undefined}
            onKeyDown={(e) => можно && e.key === 'Enter' && onOpenTask(t.id)}
            title={можно ? 'Открыть задачу' : 'Чужая задача — открыть нельзя'}
          >
            <div className="tkw-task-top">
              <span className="task-num">№ {t.id}</span>
              <span className="tkw-task-title">{t.title}</span>
              {t.is_support && <span className="support-chip">Сопровождение</span>}
            </div>
            <div className="tkw-task-sub">
              <span>{t.assignee_name ?? 'никто не назначен'}</span>
              <span>{WORK_STATUS_LABEL[t.work_status]}</span>
              <span>оценка: {t.estimate ?? '—'} ч</span>
              <span>наработано: {t.hours || 0} ч</span>
            </div>
          </div>
        )
      })}

      {mayReply && !adding && (
        <button className="btn small" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>
          + Передать специалисту
        </button>
      )}

      {adding && (
        <div className="grid-2" style={{ marginTop: 8 }}>
          <div className="field">
            <label htmlFor="hand-who">Кому передать</label>
            <select
              id="hand-who"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              autoFocus
            >
              <option value="">— выберите —</option>
              {staff.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="hand-est">Оценка, часов</label>
            <input
              id="hand-est"
              type="number"
              step="0.5"
              min="0"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              placeholder="если уже понятно"
            />
          </div>
          <div className="field full">
            <button
              className="btn primary"
              disabled={busy || !assignee}
              onClick={() => void hand()}
            >
              {busy ? 'Передаём…' : 'Передать'}
            </button>{' '}
            <button className="btn" onClick={() => setAdding(false)}>
              Отмена
            </button>
            <span className="hint-inline" style={{ display: 'block', marginTop: 6 }}>
              Задача встанет в журнал специалиста. Заявка перейдёт в «Принято».
            </span>
          </div>
        </div>
      )}
    </>
  )
}
